/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope; pack variants carry the pack choice.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 */

const numericId = (id) => String(id || '').split('/').pop();

/**
 * Estado de los packs de la página: el composer de la app responde a
 * `crioh:pack-request-state` emitiendo `crioh:pack-change` en el momento.
 * @returns {Array<Record<string, any>>}
 */
function packStates() {
  /** @type {Array<Record<string, any>>} */
  const states = [];
  /** @param {Event} event */
  const collect = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail) states.push(detail);
  };
  document.addEventListener('crioh:pack-change', collect);
  document.dispatchEvent(new CustomEvent('crioh:pack-request-state'));
  document.removeEventListener('crioh:pack-change', collect);
  return states;
}

/** @param {{lines?: Array<Record<string, any>>}} input */
function addPackProperties(input) {
  const lines = (input.lines || []).filter((line) => line.merchandiseId && !line.attributes);
  if (!lines.length) return;
  const states = packStates();
  for (const line of lines) {
    const pack = states.find((state) => numericId(state.parentVariantId) === numericId(line.merchandiseId));
    if (!pack) continue;
    if (!pack.allOk) throw new Error('Pack no disponible: elige una opción con stock en cada producto del pack');
    line.attributes = [
      { key: '_pack_id', value: String(pack.packId) },
      { key: '_pack_title', value: String(pack.packTitle || '') },
      { key: '_pack_choices', value: (pack.choicePairs || []).join(';') },
      { key: '_pack_lines', value: JSON.stringify(pack.packLines || []) },
      { key: '_pack_total_cents', value: String((pack.totals && pack.totals.finalCents) || 0) },
    ];
  }
}

function init() {
  const actions = window.Shopify && window.Shopify.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('cart-drawer-component') ?? document,
    // Crioh Packs: un agente que añade la variante de un pack con updateCart se lleva
    // la elección de la ficha (_pack_*), igual que el botón «Añadir pack».
    async handler(run, input) {
      addPackProperties(input);
      return run();
    },
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('cart-drawer-component');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify && window.Shopify.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}
